const GIFT_KEY_RE = /gift|message|inscription|card|greeting|dedication/i;
const GIFT_TEXT_RE =
  /\b(to|dear|love|lovely|happy|birthday|christmas|xmas|congratulations|congrats|anniversary|thank you|thinking of you|best wishes|miss you)\b|x{2,}\b/i;
const OPERATIONAL_RE =
  /\b(deliver|delivery|dispatch|driver|leave at|safe place|door|gate|entrance|call|ring|loading|invoice|allerg|refrigerat|before \d|after \d|access code)\b/i;
const DATE_KEY_RE = /^(delivery|dispatch|date|window|delivery window)$/i;

export const getGiftCardOrderKey = (order) =>
  String(order?.source_id || order?.sourceId || order?.orderId || order?.id || '')
    .trim()
    .replace(/^#/, '');

const normalizeText = (value) =>
  String(value ?? '')
    .replace(/\r\n?/g, '\n')
    .trim();

function collectOrderNoteValues(order) {
  const values = [];
  const checkout = order?.checkout_data || order?.checkoutData;
  if (checkout && typeof checkout === 'object' && !Array.isArray(checkout)) {
    Object.entries(checkout).forEach(([key, value]) => {
      const text = normalizeText(value);
      if (!text || DATE_KEY_RE.test(String(key).trim())) return;
      values.push({ field: `checkout_data.${key}`, label: key, text });
    });
  }

  ['notes', 'note', 'gift_message', 'giftMessage', 'inscription'].forEach((key) => {
    const text = normalizeText(order?.[key]);
    if (text) values.push({ field: key, label: key.replace(/_/g, ' '), text });
  });

  if (Array.isArray(order?.order_notes)) {
    order.order_notes.forEach((note, index) => {
      const text = normalizeText(
        typeof note === 'string'
          ? note
          : note?.message || note?.content || note?.note || note?.text,
      );
      if (text) values.push({ field: `order_notes.${index}`, label: 'Order note', text });
    });
  }
  return values;
}

function scoreCandidate(order, candidate) {
  let score = 0;
  const reasons = [];
  if (GIFT_KEY_RE.test(candidate.label) || GIFT_KEY_RE.test(candidate.field)) {
    score += 5;
    reasons.push('gift field');
  } else if (/additional comments/i.test(candidate.label)) {
    score += 2;
    reasons.push('additional comments');
  } else if (/notes?/i.test(candidate.label)) {
    score += 1;
  }
  if (GIFT_TEXT_RE.test(candidate.text)) {
    score += 4;
    reasons.push('gift wording');
  }
  if (OPERATIONAL_RE.test(candidate.text)) {
    score -= 4;
    reasons.push('possible delivery instruction');
  }
  const productNames = (order?.order_items || order?.items || [])
    .map((item) => item?.name || '')
    .join(' ');
  if (/\bgift|hamper|feast box|subscription\b/i.test(productNames)) {
    score += 1;
    reasons.push('gift-type product');
  }
  if (candidate.text.length > 240) score -= 1;
  return { score, reasons };
}

/**
 * Broadly scan order text, then score it. Nothing is discarded except empty
 * values and known date-only checkout fields; the user gets the final choice.
 */
export function findGiftCardCandidates(orders) {
  const seen = new Set();
  const candidates = [];
  (Array.isArray(orders) ? orders : []).forEach((order) => {
    const orderKey = getGiftCardOrderKey(order);
    collectOrderNoteValues(order).forEach((candidate, index) => {
      const dedupeKey = `${orderKey}::${candidate.text.toLowerCase()}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      const { score, reasons } = scoreCandidate(order, candidate);
      candidates.push({
        id: `${orderKey}:${candidate.field}:${index}`,
        order,
        orderKey,
        sourceLabel: candidate.label,
        message: candidate.text,
        score,
        likely: score >= 4,
        reasons,
      });
    });
  });
  return candidates.sort((a, b) => {
    if (a.likely !== b.likely) return a.likely ? -1 : 1;
    if (a.orderKey !== b.orderKey) return a.orderKey.localeCompare(b.orderKey);
    return b.score - a.score;
  });
}

export default findGiftCardCandidates;
